import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { ExportMenu } from "../../components/ExportMenu";
import { formatPT } from "../../lib/format";

const OUTCOMES = [
  { v: "all", label: "All" },
  { v: "saved", label: "Saved" },
  { v: "validation_error", label: "Validation error" },
  { v: "honeypot", label: "Honeypot" },
  { v: "server_error", label: "Server error" },
  { v: "client_report", label: "Client report" },
  { v: "network_fallback", label: "Network fallback" },
];
const RANGES = [
  { v: 7, label: "Last 7 days" },
  { v: 30, label: "Last 30 days" },
  { v: 0, label: "All" },
];

// Browser + OS from a user agent — full string stays on the title attribute.
export function shortUA(ua = "") {
  const s = ua || "";
  const browser = /Instagram/i.test(s) ? "Instagram"
    : /FBAN|FBAV/i.test(s) ? "Facebook"
    : /Edg\//.test(s) ? "Edge"
    : /OPR\//.test(s) ? "Opera"
    : /SamsungBrowser/.test(s) ? "Samsung"
    : /Firefox\//.test(s) ? "Firefox"
    : /CriOS/.test(s) ? "Chrome"
    : /Chrome\//.test(s) ? "Chrome"
    : /Safari\//.test(s) ? "Safari"
    : s ? "Other" : "—";
  const os = /iPhone/.test(s) ? "iPhone" : /iPad/.test(s) ? "iPad" : /Android/.test(s) ? "Android"
    : /Mac OS X/.test(s) ? "Mac" : /Windows/.test(s) ? "Windows" : /Linux/.test(s) ? "Linux" : "";
  return os ? `${browser} · ${os}` : browser;
}

const CHIP = {
  saved: { bg: "#E3EFE6", fg: "#2E6F40", label: "saved" },
  honeypot: { bg: "#ECE9E3", fg: "#857E72", label: "flagged" },
  validation_error: { bg: "#FBEBD6", fg: "#9A5B10", label: "validation error" },
  client_report: { bg: "#FBEBD6", fg: "#9A5B10", label: "client report" },
  network_fallback: { bg: "#F3DEE1", fg: "#6E2433", label: "network fallback" },
  server_error: { bg: "#F3DEE1", fg: "#6E2433", label: "server error" },
};

const OutcomeChip = ({ outcome }) => {
  const c = CHIP[outcome] || { bg: "#ECE9E3", fg: "#857E72", label: outcome || "—" };
  return (
    <span className="inline-block px-2 py-0.5 font-sans text-[11px] uppercase tracking-wide whitespace-nowrap"
      style={{ background: c.bg, color: c.fg }} data-testid={`attempt-outcome-${outcome}`}>
      {c.label}
    </span>
  );
};

export default function SignupAttempts() {
  const [rows, setRows] = useState([]);
  const [waiting, setWaiting] = useState(0);
  const [outcome, setOutcome] = useState("all");
  const [days, setDays] = useState(30);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const qs = new URLSearchParams({ outcome, ...(days ? { days: String(days) } : {}) }).toString();
    const r = await api.get(`/admin/signup-attempts?${qs}`);
    setRows(r.data.rows || []);
    setWaiting(r.data.email_waiting || 0);
    setLoaded(true);
  }, [outcome, days]);

  useEffect(() => { load().catch(() => setLoaded(true)); }, [load]);

  const printCols = [
    { key: "created_at_pacific", label: "Time (PT)" },
    { key: "outcome", label: "Outcome" },
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "class_title", label: "Class" },
    { key: "reason", label: "Reason / error" },
    { key: "user_agent", label: "Browser", render: (r) => shortUA(r.user_agent) },
    { key: "origin", label: "Origin" },
  ];

  return (
    <div className="space-y-6" data-testid="signup-attempts">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="overline mb-2">Every attempt to sign up on /join — nothing is lost silently</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">Sign-up attempts</h1>
          <p className="font-sans text-sm text-stone mt-2 max-w-xl">
            One row per submission, including the ones that failed, were flagged as bots, or were reported by the browser when the request never reached the server.
          </p>
        </div>
        <div className="no-print">
          <ExportMenu list="signup-attempts" params={{ outcome, days: days || "" }} title="Sign-up attempts" columns={printCols} rows={rows} />
        </div>
      </div>

      {waiting > 0 && (
        <p className="font-sans text-sm px-4 py-3 border" style={{ background: "#FBEBD6", borderColor: "#E8C9A0", color: "#9A5B10" }} data-testid="email-waiting-notice">
          Email notifications are not sending yet — {waiting} waiting
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-3 no-print">
        <div className="chip-row" data-testid="attempt-filter-outcome">
          {OUTCOMES.map((o) => (
            <button key={o.v} onClick={() => setOutcome(o.v)}
              className={`filter-chip px-3 py-1 text-xs font-sans border ${outcome === o.v ? "border-brass text-ink bg-brass/10" : "border-hairline text-stone"}`}
              data-testid={`attempt-filter-${o.v}`}>
              {o.label}
            </button>
          ))}
        </div>
        <select className="field-input !py-1.5 text-sm w-full sm:w-44" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="attempt-filter-range">
          {RANGES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
        </select>
      </div>

      <div className="paper-card p-0 overflow-x-auto">
        <table className="w-full text-left" data-testid="signup-attempts-table">
          <thead>
            <tr className="border-b border-hairline">
              {["Time (PT)", "Outcome", "Name", "Email", "Phone", "Class", "Reason / error", "Browser", "Origin"].map((h) => (
                <th key={h} className="overline px-3 py-3 font-normal whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-hairline last:border-0 align-top" data-testid={`attempt-row-${r.id}`}>
                <td className="px-3 py-2.5 font-sans text-xs text-stone whitespace-nowrap">{r.created_at_pacific || formatPT(r.created_at)}</td>
                <td className="px-3 py-2.5"><OutcomeChip outcome={r.outcome} /></td>
                <td className="px-3 py-2.5 font-sans text-xs text-ink">{r.name || "—"}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-ink break-all">{r.email || "—"}</td>
                <td className="px-3 py-2.5 font-mono text-xs text-stone whitespace-nowrap">{r.phone || "—"}</td>
                <td className="px-3 py-2.5 font-sans text-xs text-stone">{r.class_title || "—"}</td>
                <td className="px-3 py-2.5 font-sans text-xs text-stone max-w-[260px] break-words">
                  {r.reason || (r.http_status ? `HTTP ${r.http_status}` : "—")}
                  {r.email_notify && <span className="block text-[11px] mt-1">email: {r.email_notify.status}{r.email_confirm ? ` / ${r.email_confirm.status}` : ""}</span>}
                </td>
                <td className="px-3 py-2.5 font-sans text-xs text-stone whitespace-nowrap" title={r.user_agent || ""}>{shortUA(r.user_agent)}</td>
                <td className="px-3 py-2.5 font-mono text-[11px] text-stone break-all">{r.origin || "—"}</td>
              </tr>
            ))}
            {loaded && rows.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-6 font-sans text-sm text-stone">Nothing here yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
